import { appConfig } from '@/shared/config/app-config';
import { getEncodedData } from '@/shared/lib/cognito-threat-protection';

export type SignInErrorCode = 'INVALID_CREDENTIALS' | 'RATE_LIMITED' | 'ACCOUNT_LOCKED' | 'UNKNOWN';

export type SignInResult =
    | { ok: true; returnUrl?: string }
    | { ok: false; code: SignInErrorCode; email: string; retryAfterSeconds?: number };

export async function signInWithPassword(input: {
    email: string;
    password: string;
    role: string;
    returnUrl?: string;
}): Promise<SignInResult> {
    let response: Response;
    try {
        response = await fetch(`${appConfig.apiUrl}/auth/sign-in-with-password`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email: input.email,
                password: input.password,
                role: input.role,
                returnUrl: input.returnUrl,
                encodedData: getEncodedData(input.email),
            }),
        });
    } catch {
        return { ok: false, code: 'UNKNOWN', email: input.email };
    }

    if (response.ok) {
        const data = (await response.json().catch(() => null)) as { returnUrl?: string } | null;
        return { ok: true, returnUrl: data?.returnUrl };
    }

    if (response.status === 401) {
        return { ok: false, code: 'INVALID_CREDENTIALS', email: input.email };
    }

    if (response.status === 423) {
        return { ok: false, code: 'ACCOUNT_LOCKED', email: input.email };
    }

    if (response.status === 429) {
        const body = (await response.json().catch(() => null)) as { retryAfter?: number } | null;
        return {
            ok: false,
            code: 'RATE_LIMITED',
            email: input.email,
            retryAfterSeconds: body?.retryAfter,
        };
    }

    return { ok: false, code: 'UNKNOWN', email: input.email };
}
